import { formatUsPhone, queryKeys } from '@mevaser/shared'
import { Button, Input } from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { UserPlus } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'

export interface CustomerLite {
  id: string
  name: string
  businessName?: string | null
  phone?: string | null
}

function subtitle(customer: CustomerLite): string {
  return (
    [customer.businessName, customer.phone ? formatUsPhone(customer.phone) : null]
      .filter(Boolean)
      .join(' · ') || '—'
  )
}

/** Search-as-you-type customer chooser: a box that turns into the chosen
 *  customer with a Change button. With `onCreate`, a search always offers to
 *  create that customer: as the last entry under the matches, or on its own
 *  when nobody matches. */
export function CustomerPicker({
  value,
  onChange,
  placeholder = 'Name, identifier, or phone…',
  onCreate,
}: {
  value: CustomerLite | null
  onChange: (customer: CustomerLite | null) => void
  placeholder?: string
  onCreate?: (search: string) => void
}) {
  const [search, setSearch] = useState('')
  const searching = search.trim().length >= 2

  const { data, isFetching } = useQuery({
    queryKey: queryKeys.customers.list({ q: search }),
    queryFn: () =>
      api.get<{ items: CustomerLite[] }>('/customers', { query: { q: search, pageSize: 6 } }),
    enabled: searching && !value,
  })

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{value.name}</p>
          {value.businessName || value.phone ? (
            <p className="font-identifier text-xs text-foreground-muted">{subtitle(value)}</p>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        aria-label="Search customers"
      />
      {data?.items.length ? (
        <ul className="divide-y divide-border rounded-md border border-border">
          {data.items.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="w-full px-2 py-2 text-start text-sm hover:bg-surface-sunken"
                onClick={() => {
                  onChange(row)
                  setSearch('')
                }}
              >
                <span className="block font-medium">{row.name}</span>
                <span className="block font-identifier text-2xs text-foreground-muted">
                  {subtitle(row)}
                </span>
              </button>
            </li>
          ))}
          {onCreate ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2 py-2 text-start text-sm font-medium hover:bg-surface-sunken"
                onClick={() => onCreate(search.trim())}
              >
                <UserPlus className="size-4" /> Create "{search.trim()}"
              </button>
            </li>
          ) : null}
        </ul>
      ) : data && searching && !isFetching ? (
        <div className="space-y-2 px-1">
          <p className="text-sm text-foreground-muted">No customers match.</p>
          {onCreate ? (
            <Button variant="secondary" size="sm" onClick={() => onCreate(search.trim())}>
              <UserPlus /> Create "{search.trim()}"
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
